import React, { useState, useEffect } from 'react';

// Helper function to create a unique path for an element
const getElementPath = (element) => {
  const path = [];
  let current = element;
  
  while (current && current !== document.body) {
    let selector = current.tagName.toLowerCase();
    if (current.id) {
      selector += `#${current.id}`;
    } else if (current.className) {
      selector += `.${current.className.split(' ').join('.')}`;
    }
    
    // Add sibling index to make it unique
    const siblings = Array.from(current.parentElement?.children || []);
    const index = siblings.indexOf(current);
    if (index > 0) {
      selector += `:nth-child(${index + 1})`;
    }
    
    path.unshift(selector);
    current = current.parentElement;
  }
  
  return path.join(' > ');
};

const SimpleElementSelector = ({ isVisible = false, onElementSelected }) => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [hoveredElement, setHoveredElement] = useState(null);

  useEffect(() => {
    if (!isVisible) return;

    const handleMouseOver = (e) => {
      if (!isSelecting) return;
      
      // Highlight the hovered element
      e.target.style.outline = '3px solid #ffff00';
      e.target.style.outlineOffset = '2px';
      setHoveredElement({
        element: e.target,
        tagName: e.target.tagName,
        className: e.target.className,
        id: e.target.id,
        textContent: e.target.textContent?.slice(0, 20) || '',
        rect: e.target.getBoundingClientRect()
      });
    };

    const handleMouseOut = (e) => {
      if (!isSelecting) return;
      e.target.style.outline = '';
      e.target.style.outlineOffset = '';
    };

    const handleClick = (e) => {
      if (!isSelecting) return;
      e.preventDefault();
      e.stopPropagation();
      
      // Remove outline
      e.target.style.outline = '';
      e.target.style.outlineOffset = '';
      
      // Get element info with more context
      const elementInfo = {
        tagName: e.target.tagName,
        className: e.target.className,
        id: e.target.id,
        textContent: e.target.textContent?.slice(0, 20) || '',
        rect: e.target.getBoundingClientRect(),
        // Add position info to distinguish between player/opponent
        position: {
          x: e.target.getBoundingClientRect().x,
          y: e.target.getBoundingClientRect().y
        },
        // Add parent context
        parentInfo: e.target.parentElement ? {
          className: e.target.parentElement.className,
          tagName: e.target.parentElement.tagName
        } : null,
        // Add unique identifier based on DOM path
        uniquePath: getElementPath(e.target)
      };
      
      // Call the callback with element info
      if (onElementSelected) {
        onElementSelected(elementInfo);
      }
      
      setIsSelecting(false);
    };

    if (isSelecting) {
      document.addEventListener('mouseover', handleMouseOver, true);
      document.addEventListener('mouseout', handleMouseOut, true);
      document.addEventListener('click', handleClick, true);
    }

    return () => {
      document.removeEventListener('mouseover', handleMouseOver, true);
      document.removeEventListener('mouseout', handleMouseOut, true);
      document.removeEventListener('click', handleClick, true);
    };
  }, [isVisible, isSelecting, onElementSelected]);

  if (!isVisible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 10,
        right: 10,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        color: 'white',
        padding: '10px',
        borderRadius: '8px',
        border: '2px solid #ffff00',
        zIndex: 10001,
        minWidth: '200px'
      }}
    >
      <div style={{ color: '#ffff00', fontWeight: 'bold', marginBottom: '10px' }}>
        🎯 Element Selector
      </div>
      
      <button
        onClick={() => setIsSelecting(!isSelecting)}
        style={{
          backgroundColor: isSelecting ? '#ff4444' : '#ffff00',
          color: 'white',
          border: 'none',
          padding: '8px 16px',
          borderRadius: '4px',
          cursor: 'pointer',
          fontWeight: 'bold',
          marginBottom: '10px'
        }}
      >
        {isSelecting ? 'Click to Select Element' : 'Start Selecting'}
      </button>
      
      {isSelecting && (
        <div style={{ color: '#ffaa00', fontSize: '12px', marginBottom: '10px' }}>
          Hover over elements to highlight them, then click to select
        </div>
      )}
      
      {hoveredElement && (
        <div style={{ fontSize: '10px', color: '#ccc' }}>
          <div><strong>Tag:</strong> {hoveredElement.tagName}</div>
          <div><strong>Class:</strong> {hoveredElement.className || 'none'}</div>
          <div><strong>ID:</strong> {hoveredElement.id || 'none'}</div>
          <div><strong>Text:</strong> {hoveredElement.textContent}</div>
        </div>
      )}
    </div>
  );
};

export default SimpleElementSelector;
